import {
  type Mutation,
  type UseMutationResult,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  invalidateCatalogQueries,
  type ThreadPostState,
  type ThreadState,
} from "@/entities/thread";
import { threadStateQueryKey } from "@/features/thread/hooks/threadChunkQuery";
import { apiDelete, useAimgViewerContext } from "@/shared/api";
import {
  markThreadPostAsTrash,
  restoreThreadPostState,
  shouldApplyOptimisticPostDeletion,
  shouldInvalidateThreadStateAfterDelete,
} from "@/shared/hooks/optimisticPostDeletion";
import { getApiErrorMessage } from "@/shared/ui/feedback/apiErrorMessage";
import { toast } from "@/shared/ui/toast";

interface DeleteResponse {
  postId: string;
  threadDeleted: boolean;
}

interface DeleteParams {
  password: string;
  threadId: string;
  postId: string;
  postSeq: number;
}

interface DeleteMutationContext {
  appliedOptimisticState: boolean;
  previousPostState: ThreadPostState | undefined;
  previousState: ThreadState | undefined;
  queryKey: ReturnType<typeof threadStateQueryKey>;
  toastId: string;
}

const DELETE_POST_MUTATION_KEY = ["delete-post"] as const;

export function useDeleteMutation(): UseMutationResult<
  DeleteResponse,
  unknown,
  DeleteParams,
  DeleteMutationContext
> {
  const queryClient = useQueryClient();
  const viewerContext = useAimgViewerContext();

  return useMutation({
    mutationKey: DELETE_POST_MUTATION_KEY,
    mutationFn: async ({
      password,
      postId,
    }: DeleteParams): Promise<DeleteResponse> => {
      return apiDelete<DeleteResponse>(
        `/posts/${postId}`,
        { deleteKey: password },
        { requiresToken: true },
      );
    },
    onMutate: async (params: DeleteParams): Promise<DeleteMutationContext> => {
      const queryKey = threadStateQueryKey(params.threadId, viewerContext);
      await queryClient.cancelQueries({ queryKey, exact: true });
      const previousState = queryClient.getQueryData<ThreadState>(queryKey);
      const previousPostState = previousState?.postStates.find(
        (postState) => postState.seq === params.postSeq,
      );
      const appliedOptimisticState = Boolean(
        params.postSeq > 0 &&
          previousState &&
          shouldApplyOptimisticPostDeletion(previousState, params.postSeq),
      );
      const toastId = toast.loading(
        params.postSeq === 0
          ? "スレッドを削除しています"
          : "レスを削除しています",
      );

      if (appliedOptimisticState && previousState) {
        queryClient.setQueryData<ThreadState>(
          queryKey,
          markThreadPostAsTrash(previousState, params.postSeq),
        );
      }

      return {
        appliedOptimisticState,
        previousPostState,
        previousState,
        queryKey,
        toastId,
      };
    },
    onSuccess: (
      data: DeleteResponse,
      _params: DeleteParams,
      context: DeleteMutationContext,
    ): void => {
      if (data.threadDeleted) {
        void invalidateCatalogQueries(queryClient);
      }
      toast.success(
        data.threadDeleted ? "スレッドを削除しました" : "レスを削除しました",
        {
          id: context.toastId,
        },
      );
    },
    onError: (
      error: unknown,
      params: DeleteParams,
      context: DeleteMutationContext | undefined,
    ): void => {
      if (context?.appliedOptimisticState && context.previousState) {
        queryClient.setQueryData<ThreadState>(context.queryKey, (state) =>
          state
            ? restoreThreadPostState(
                state,
                params.postSeq,
                context.previousPostState,
              )
            : context.previousState,
        );
      }
      toast.error(getApiErrorMessage(error, "削除に失敗しました"), {
        id: context?.toastId,
      });
    },
    onSettled: (
      _data: DeleteResponse | undefined,
      _error: unknown,
      params: DeleteParams,
    ): void => {
      const activeThreadDeletes = queryClient.isMutating({
        mutationKey: DELETE_POST_MUTATION_KEY,
        exact: true,
        predicate: (mutation: Mutation<unknown, unknown, unknown, unknown>) =>
          isSameThreadDelete(mutation.state.variables, params.threadId),
      });
      if (shouldInvalidateThreadStateAfterDelete(activeThreadDeletes)) {
        void queryClient.invalidateQueries({
          queryKey: threadStateQueryKey(params.threadId, viewerContext),
          exact: true,
        });
      }
    },
  });
}

function isSameThreadDelete(value: unknown, threadId: string): boolean {
  return (
    typeof value === "object" &&
    value !== null &&
    "threadId" in value &&
    value.threadId === threadId
  );
}
